<script lang="ts">
  import Icon from './primitives/Icon.svelte';
  /* THE CHART CACHE, from the cockpit.
   *
   * The pi plugin has its own admin page and it owns the heavy operations —
   * seeding an area before a passage, purging, compacting. This is the other
   * question, the one you ask while the chart is slow and you are holding the
   * iPad: is anything cached, is it being used, and which of the two caches is
   * answering.
   *
   * ═══ TWO CACHES, AND THE DIFFERENCE MATTERS AT SEA ═══
   *
   * THIS DEVICE is IndexedDB, per browser, checked before any request is
   * issued. THE BOAT is SQLite on the pi, shared by every device, WebP-compacted
   * and de-duplicated, kept for ever. A tile has three chances to avoid the
   * internet: this device, the pi, then CHS.
   *
   * Which one is empty tells you what to do. An empty device cache on a laptop
   * you just opened is normal. An empty BOAT cache before a passage means the
   * seeding never ran, and that is worth knowing at the dock rather than in
   * Johnstone Strait.
   *
   * The boat half greys out with a reason on a host that has no pi, exactly as
   * a layer does — see chart-core's cachestats.
   */
  import { cacheStats } from '@gennetay/chart-core/map/cachestats.svelte';

  let { onclose }: { onclose?: () => void } = $props();

  let confirming = $state(false);
  let cleared = $state(false);

  $effect(() => {
    void cacheStats.refresh().then(() => (settled = true));
    /* Slow on purpose. These are disk figures, not instruments: the pi's
     * snapshot is itself refreshed on a timer, and a fast poll would spend the
     * boat's wifi on a number nobody is watching change. */
    const t = setInterval(() => void cacheStats.refresh(), 10_000);
    return () => clearInterval(t);
  });

  /* Binary units, because that is what a browser's quota is quoted in and the
   * two figures sit next to each other. */
  const bytes = (n: number | undefined): string => {
    if (n === undefined || !Number.isFinite(n)) return '—';
    const u = ['B', 'kB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < u.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
  };
  const count = (n: number | undefined): string =>
    n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en');
  const pct = (n: number | undefined): string =>
    n === undefined || !Number.isFinite(n) ? '—' : `${Math.round(n * 100)}%`;

  const device = $derived(cacheStats.device);
  const server = $derived(cacheStats.server);
  /* ═══ "NOT YET" IS NOT "NO" ═══
   *
   * `device` and `server` are null both before the first refresh resolves and
   * when there is genuinely nothing to report, and the first render is always
   * the former. Showing "this browser is not storing tiles" for the half-second
   * before the figures arrive is a panel that accuses the browser of a fault it
   * does not have — and it is the first thing anyone sees. */
  let settled = $state(false);
  /** Of the tiles the pi knows about, how many are actual chart. The rest are
   *  squares CHS says are empty — worth showing, because a cache that looks
   *  large can be mostly "there is no chart here". */
  const chartedPct = $derived(
    server && server.tiles > 0 ? server.charted / server.tiles : undefined
  );

  async function clearDevice() {
    if (!confirming) {
      confirming = true;
      return;
    }
    await cacheStats.clearDevice();
    confirming = false;
    cleared = true;
    setTimeout(() => (cleared = false), 4000);
  }
</script>

<section class="cache">
  <header>
    <span>CHART CACHE</span>
    {#if onclose}
      <button class="x" onclick={onclose} aria-label="close"><Icon name="close" size={14} /></button>
    {/if}
  </header>

  <div class="scroll">
    <p class="note">
      A tile has three chances to avoid the internet: this device, then the
      boat, then the chart service.
    </p>

    <h3>THIS DEVICE</h3>
    {#if !cacheStats.hasServer}
      <!-- ═══ THE CASE THAT LOOKS LIKE A BROKEN CACHE AND IS NOT ═══
           With no tile proxy the chart layer talks to the WMS directly, which
           MapLibre loads itself — so the figures below stay at zero however
           long you pan, because nothing goes through our protocol to be cached.
           Saying "0 tiles" without saying that reads as a fault. -->
      <p class="off">
        not in use — with no tile proxy the chart is fetched straight from the
        service, so nothing passes through this cache
      </p>
      <p class="note">
        And the browser will not hold them either: the chart service answers
        every tile with <code>max-age=0</code>, which is the whole reason the
        boat runs a proxy. Every pan is a fresh download.
      </p>
    {:else if device}
      <dl>
        <div><dt>tiles held</dt><dd>{count(device.tiles)}</dd></div>
        <div><dt>storage used</dt><dd>{bytes(device.usage)} of {bytes(device.quota)}</dd></div>
        <div>
          <dt>served from here</dt>
          <dd>
            {count(device.hit)}
            <span class="sub">{pct(cacheStats.deviceHitRate)} of requests this session</span>
          </dd>
        </div>
        <div><dt>fetched</dt><dd>{count(device.miss)}</dd></div>
        <div>
          <dt>failed</dt>
          <dd class:warn={device.failed > 0}>{count(device.failed)}</dd>
        </div>
      </dl>
      <p class="note">
        Counted since this page loaded, not for ever — a lifetime total would
        hide a cache that stopped working today.
      </p>
      <div class="acts">
        <button class="btn" class:arm={confirming} onclick={clearDevice}>
          {confirming ? 'REALLY CLEAR?' : 'CLEAR THIS DEVICE'}
        </button>
        {#if confirming}
          <button class="btn quiet" onclick={() => (confirming = false)}>CANCEL</button>
        {/if}
        {#if cleared}<span class="ok">cleared</span>{/if}
      </div>
      <p class="note">
        The boat's cache is not touched — it is shared by every device aboard,
        and the plugin's own page owns it.
      </p>
    {:else if !settled}
      <p class="off">reading…</p>
    {:else}
      <p class="off">
        this browser is not storing tiles — private browsing, or storage refused
      </p>
    {/if}

    <h3>THE BOAT</h3>
    {#if server}
      <dl>
        <div>
          <dt>squares known</dt>
          <dd>
            {count(server.tiles)}
            <span class="sub">{pct(chartedPct)} carry chart, the rest are open sea</span>
          </dd>
        </div>
        <div><dt>with chart on them</dt><dd>{count(server.charted)}</dd></div>
        <div><dt>known to be empty</dt><dd>{count(server.blanks)}</dd></div>
        <div><dt>on disk</dt><dd>{bytes(server.bytes)}</dd></div>
        <div>
          <dt>saved by compaction</dt>
          <dd>{server.savedPct === undefined ? '—' : `${Math.round(server.savedPct)}%`}</dd>
        </div>
        <div><dt>served from the boat</dt><dd>{pct(server.hitRate)}</dd></div>
      </dl>
      <p class="note">
        Kept for ever and shared by every device aboard. Seeding an area before a
        passage, purging and compacting live on the plugin's own admin page.
      </p>
    {:else if !settled && cacheStats.hasServer}
      <p class="off">reading…</p>
    {:else}
      <p class="off">{cacheStats.serverError ?? 'no figures'}</p>
      {#if !cacheStats.hasServer}
        <p class="note">
          Nothing is wrong — this host simply has no proxy. Deploying one is
          what would give either cache something to hold.
        </p>
      {/if}
    {/if}
  </div>
</section>

<style>
  /* Every colour is a token WITH A FALLBACK, so this looks right dropped into
     the dash's design system, into apps/web's four variables, or into a host
     with neither. Svelte scopes all of it, so nothing leaks either way. */
  .cache {
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: var(--panel);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 8px;
  }
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid var(--line);
    font: 11px/1 var(--mono);
    letter-spacing: 0.08em;
    color: var(--sub);
  }
  .x {
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    font-size: 13px;
    padding: 2px 4px;
  }
  .scroll {
    overflow-y: auto;
    padding: 4px 10px 12px;
  }
  h3 {
    font: 10px/1 var(--mono);
    letter-spacing: 0.1em;
    color: var(--sub);
    margin: 14px 0 6px;
  }
  dl {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  dl > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }
  dt {
    font: 11px/1.3 var(--sans, system-ui, sans-serif);
    color: var(--sub);
    white-space: nowrap;
  }
  code {
    font: 10px/1 var(--mono);
    opacity: 0.85;
  }
  dd {
    margin: 0;
    text-align: right;
    font: 12px/1.3 var(--mono);
    /* The figures are the point; let a long one wrap rather than clip. */
    min-width: 0;
  }
  .sub {
    display: block;
    font: 10px/1.3 var(--sans, system-ui, sans-serif);
    color: var(--sub);
  }
  .warn {
    color: var(--am);
  }
  .note {
    font: 10px/1.45 var(--sans, system-ui, sans-serif);
    color: var(--sub);
    margin: 8px 0 0;
  }
  .off {
    font: 11px/1.4 var(--sans, system-ui, sans-serif);
    color: var(--sub);
    opacity: 0.75;
    margin: 4px 0 0;
  }
  .acts {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
  }
  .btn {
    padding: 6px 10px;
    border-radius: 5px;
    border: 1px solid var(--line);
    background: transparent;
    color: var(--ink);
    font: 10px/1 var(--mono);
    letter-spacing: 0.06em;
    cursor: pointer;
  }
  /* Armed, not destructive-looking by default: the first press asks. */
  .btn.arm {
    border-color: var(--am);
    color: var(--am);
  }
  .btn.quiet {
    opacity: 0.6;
  }
  .ok {
    font: 10px/1 var(--sans, system-ui, sans-serif);
    color: var(--sub);
  }

  /* ═══ A PHONE, WHICH IS NOT A WIDTH ═══
   *
   * Held sideways one is 734-852 px wide and under 400 tall, so the query asks
   * about both. The boat's iPad is 1024 x 768 and matches neither arm, so it
   * keeps the denser control its own layout and its own proofs are built
   * around. */
  @media (pointer: coarse) and (max-width: 640px),
    (pointer: coarse) and (max-height: 500px) {
    /* `CLEAR THIS DEVICE` measured 134 x 24 on an iPhone. It is the only
       destructive control in this panel, and Hoober's point about destructive
       actions is that nobody taps where they mean to — a 24 px target for
       "throw away the tile cache" is the wrong way round. */
    .btn {
      min-height: 44px;
      padding: 12px 14px;
    }
  }
</style>
